import React from 'react';
import { Card, Form, Alert } from 'react-bootstrap';

interface Question {
  id: number;
  text: string;
  type: 'yes_no';
  required: boolean;
  choices?: {
    id: number;
    text: string;
    value: number;
  }[];
}

interface SCIDQuestionnaireProps {
  questions: Question[];
  language: 'en' | 'el';
  answers: Record<number, any>;
  onAnswerChange: (questionId: number, answer: any) => void;
}

export function SCIDQuestionnaire({ 
  questions, 
  language, 
  answers, 
  onAnswerChange 
}: SCIDQuestionnaireProps) {
  
  const handleAnswerChange = (questionId: number, value: string) => {
    console.log('SCID Answer changed:', { questionId, value });
    onAnswerChange(questionId, value);
  };

  // Create sections based on SCID PDF structure
  const createSections = () => {
    const sections = [];
    
    // Section 1: Questions 1-91 (Regular personality disorder questions)
    const regularQuestions = questions.filter(q => q.id >= 1 && q.id <= 91);
    if (regularQuestions.length > 0) {
      // Split into groups of 20 for better readability
      const questionsPerGroup = 20;
      for (let i = 0; i < regularQuestions.length; i += questionsPerGroup) {
        const group = regularQuestions.slice(i, i + questionsPerGroup);
        sections.push({
          title: language === 'el' 
            ? `Ερωτήσεις ${group[0].id} - ${group[group.length - 1].id}`
            : `Questions ${group[0].id} - ${group[group.length - 1].id}`,
          subtitle: null,
          questions: group,
          sectionType: 'regular'
        });
      }
    }
    
    // Section 2: Questions 92-104 (Before age 15)
    const age15Questions = questions.filter(q => q.id >= 92 && q.id <= 104);
    if (age15Questions.length > 0) {
      sections.push({
        title: language === 'el' 
          ? 'Ερωτήσεις 92 - 104 (Πριν την ηλικία των 15 ετών)'
          : 'Questions 92 - 104 (Before age 15)',
        subtitle: language === 'el' 
          ? 'Οι ακόλουθες ερωτήσεις αφορούν σε πράγματα που έκανες πριν από την ηλικία των 15 ετών'
          : 'The following questions concern things you did before the age of 15',
        questions: age15Questions,
        sectionType: 'age15'
      });
    }
    
    // Section 3: Questions 105-106 (Before age 13)
    const age13Questions = questions.filter(q => q.id >= 105 && q.id <= 106);
    if (age13Questions.length > 0) {
      sections.push({
        title: language === 'el' 
          ? 'Ερωτήσεις 105 - 106 (Πριν την ηλικία των 13 ετών)'
          : 'Questions 105 - 106 (Before age 13)',
        subtitle: language === 'el' 
          ? 'Οι ακόλουθες δύο ερωτήσεις αφορούν σε πράγματα που έκανες πριν από την ηλικία των 13 ετών'
          : 'The following two questions concern things you did before the age of 13',
        questions: age13Questions,
        sectionType: 'age13'
      });
    }
    
    return sections;
  };

  const sections = createSections();
  
  // Debug logging for sections
  console.log('SCID Sections created:', sections.map(s => ({
    title: s.title,
    type: s.sectionType,
    questionCount: s.questions.length,
    questionIds: s.questions.map(q => q.id)
  })));

  // Check if we have translation issues (empty text)
  const hasTranslationIssues = questions.some(q => !q.text || q.text.trim() === '');

  return (
    <>
      {/* Translation warning for English */}
      {language === 'en' && hasTranslationIssues && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Translation Notice</Alert.Heading>
          <p>
            English translations for the SCID questionnaire are not yet available. 
            The questionnaire is currently only available in Greek. 
            Please switch to Greek (ΕΛ) to view the complete questionnaire.
          </p>
        </Alert>
      )}
      
      <Card className="scid-questionnaire">
        <Card.Header>
          <h3>{language === 'el' ? 'SCID Ερωτηματολόγιο' : 'SCID Questionnaire'}</h3>
          <p className="text-muted">
            {language === 'el' 
              ? `Απαντήστε σε όλες τις ερωτήσεις (${questions.length} συνολικά)`
              : `Please answer all questions (${questions.length} total)`
            }
          </p>
          <div className="progress-info">
            <strong>
              {language === 'el' ? 'Πρόοδος: ' : 'Progress: '}
              {Object.keys(answers).length}/{questions.length}
            </strong>
          </div>
        </Card.Header>
        <Card.Body>
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={`question-section mb-5 ${section.sectionType}`}>
              <div className="section-header mb-3">
                <h5 className="section-title">
                  {section.title}
                </h5>
                {section.subtitle && (
                  <div className="section-subtitle text-muted fst-italic mb-3 p-3 bg-light rounded">
                    <i className="bi bi-info-circle me-2"></i>
                    {section.subtitle}
                  </div>
                )}
              </div>
              
              <div className="scid-table-container">
                <table className="table table-striped table-bordered table-hover table-sm scid-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>
                        {language === 'el' ? 'Ερώτηση' : 'Question'}
                      </th>
                      <th className="text-center">
                        {language === 'el' ? 'Ναι' : 'Yes'}
                      </th>
                      <th className="text-center">
                        {language === 'el' ? 'Όχι' : 'No'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.questions.map((question) => (
                      <tr key={question.id} className={answers[question.id] ? 'answered' : 'unanswered'}>
                        <td className="text-center">
                          <strong>{question.id}</strong>
                        </td>
                        <td>
                          <span className="question-text">
                            {question.text || `[Question ${question.id} - Translation not available]`}
                          </span>
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="radio"
                            id={`q${question.id}-yes`}
                            name={`question-${question.id}`}
                            value="yes"
                            checked={answers[question.id] === 'yes'}
                            onChange={() => handleAnswerChange(question.id, 'yes')}
                            className="scid-radio"
                          />
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="radio"
                            id={`q${question.id}-no`}
                            name={`question-${question.id}`}
                            value="no"
                            checked={answers[question.id] === 'no'}
                            onChange={() => handleAnswerChange(question.id, 'no')}
                            className="scid-radio"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          
          <div className="completion-summary p-3 bg-light rounded">
            <h6>{language === 'el' ? 'Σύνοψη Συμπλήρωσης' : 'Completion Summary'}</h6>
            
            {/* Overall progress */}
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>{language === 'el' ? 'Συνολικές ερωτήσεις:' : 'Total questions:'}</strong> {questions.length}
              </div>
              <div className="col-md-4">
                <strong>{language === 'el' ? 'Απαντημένες:' : 'Answered:'}</strong> {Object.keys(answers).length}
              </div>
              <div className="col-md-4">
                <strong>{language === 'el' ? 'Υπολειπόμενες:' : 'Remaining:'}</strong> {questions.length - Object.keys(answers).length}
              </div>
            </div>
            
            {/* Section-specific progress */}
            <div className="row small text-muted">
              {(() => {
                // Calculate combined progress for each section type
                const regularQuestions = questions.filter(q => q.id >= 1 && q.id <= 91);
                const age15Questions = questions.filter(q => q.id >= 92 && q.id <= 104);
                const age13Questions = questions.filter(q => q.id >= 105 && q.id <= 106);
                
                const progressSections = [
                  {
                    type: 'regular',
                    label: language === 'el' ? 'Γενικές' : 'Regular',
                    questions: regularQuestions,
                    color: 'bg-primary'
                  },
                  {
                    type: 'age15',
                    label: language === 'el' ? 'Πριν 15' : 'Before 15',
                    questions: age15Questions,
                    color: 'bg-info'
                  },
                  {
                    type: 'age13',
                    label: language === 'el' ? 'Πριν 13' : 'Before 13',
                    questions: age13Questions,
                    color: 'bg-success'
                  }
                ];
                
                return progressSections.map((progressSection, index) => {
                  const sectionAnswered = progressSection.questions.filter(q => answers[q.id] !== undefined).length;
                  const sectionTotal = progressSection.questions.length;
                  const sectionProgress = sectionTotal > 0 ? Math.round((sectionAnswered / sectionTotal) * 100) : 0;
                  
                  return (
                    <div key={index} className="col-md-4 mb-1">
                      <div className={`section-progress ${progressSection.type}`}>
                        <div className="d-flex justify-content-between">
                          <span>{progressSection.label}:</span>
                          <span>{sectionAnswered}/{sectionTotal}</span>
                        </div>
                        <div className="progress progress-sm mt-1">
                          <div 
                            className={`progress-bar ${progressSection.color}`}
                            style={{ width: `${sectionProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {Object.keys(answers).length === questions.length && (
              <div className="mt-3 text-success">
                <strong>
                  {language === 'el' 
                    ? '✓ Όλες οι ερωτήσεις έχουν απαντηθεί. Μπορείτε να υποβάλετε το ερωτηματολόγιο.'
                    : '✓ All questions answered. You can submit the questionnaire.'
                  }
                </strong>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
